import * as React from 'react';
import * as Dialog from '@naan/primitives/modals/dialog';
import { observer } from 'mobx-react';
import { namedStyled } from '@naan/stitches.config';
import { PreviewCard } from './preview-card';
import { EndCard } from './end-card';
import { CloseSmallIcon } from '@naan/icons/close-icon';
import { IconButton } from '@naan/primitives/button';
import { VideoPlayerModel } from './video-player-model';
import { VideoPlayer } from 'components/ui/video-player';
import { VideoGuideEngagementStatus } from '@core/models/user-manager/video-guide-engagement';

const Wrapper = namedStyled('VideoModalWrapper', 'div', {
  width: 'min(960px, calc(100vw - 16px), calc(100vh * 0.5625 - 8px))',
  paddingTop: (16 / 9) * 100 + '%',
  backgroundColor: '$blue-700',
  overflow: 'hidden',
  borderRadius: '$space$3',
  position: 'relative',
  '@medium': {
    width: 'min(960px, calc(100vw - 32px), calc((100vh / 0.5625) - 32px))',
    paddingTop: (9 / 16) * 100 + '%',
  },

  '& > .card': {
    position: 'absolute',
    left: 0,
    top: 0,
    width: '100%',
    height: '100%',
    overflow: 'hidden',
    borderRadius: '$space$3',
  },

  '& > .preview': {
    backgroundColor: '$blue-700',
    zIndex: 3,
    backgroundSize: 'cover',
  },

  '& > .close-btn-container': {
    position: 'absolute',
    right: 16,
    top: 16,
    zIndex: 4,
    touchEvents: 'none',
  },
});

const getCloudflareDashUrlFromId = (videoId: string) => {
  return `https://customer-4gz6yh9017ekbrye.cloudflarestream.com/${videoId}/manifest/video.mpd`;
};

export const CloseButton = observer(({ onClick }: { onClick: () => void }) => {
  return (
    <div className="close-btn-container">
      <IconButton
        presentation={'whiteDarken'}
        size={'small'}
        icon={<CloseSmallIcon />}
        onClick={onClick}
      />
    </div>
  );
});

export const VideoModal = observer(
  ({
    model,
    onDismiss,
  }: {
    model: VideoPlayerModel;
    onDismiss: () => void;
  }) => {
    const video = model.current;
    const onVideoEnd = React.useCallback(() => {
      model.userData.recordEngagement({
        slug: model.current.slug,
        status: VideoGuideEngagementStatus.PLAYED,
      });
    }, [model]);

    if (!video) {
      return null;
    }
    const src = getCloudflareDashUrlFromId(video.videoIdLandscape);
    return (
      <Dialog.Container
        open
        onDismiss={onDismiss}
        css={{ $$maxWidth: 'none', maxHeight: 'none' }}
      >
        <Wrapper className="video-modal-wrapper">
          <VideoPlayer
            src={src}
            onEnded={onVideoEnd}
            renderEndCard={player => (
              <EndCard player={player} model={model} onDismiss={onDismiss} />
            )}
            renderPreviewCard={player => (
              <PreviewCard model={model} player={player} />
            )}
          />
          <CloseButton onClick={onDismiss} />
        </Wrapper>
      </Dialog.Container>
    );
  }
);
